// CustomSelect.tsx
'use client';
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, RefObject } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cn } from '@/app/lib/utils';
import Icon, { IconName, IconProps } from '@/app/components/ui/Icon';
import Input from '@/app/components/ui/Input';
import Spinner from '@/app/components/ui/Spinner';
import Pill, { PillProps } from './Pill';

const SelectRoot = SelectPrimitive.Root;
const SelectTrigger = SelectPrimitive.Trigger;
const SelectTriggerIcon = SelectPrimitive.Icon;
const SelectContent = SelectPrimitive.Content;
const SelectItem = SelectPrimitive.Item;
const SelectPortal = SelectPrimitive.Portal;
const SelectScrollUpButton = SelectPrimitive.ScrollUpButton;
const SelectScrollDownButton = SelectPrimitive.ScrollDownButton;

export type Option = {
  id: string | number;
  value: string | number;
  show?: boolean;
  icon?: IconName;
  iconProps?: Partial<IconProps>;
  prefix?: React.ReactNode;
  meta?: Record<string, unknown>;
  disabled?: boolean;
};

export type CustomSelectProps = Omit<SelectPrimitive.SelectProps, 'defaultValue' | 'value'> & {
  options: Option[];
  placeholder?: string;
  defaultValue?: string | number | string[] | number[];
  noDataText?: string;
  triggerClassNames?: string;
  contentClassNames?: string;
  wrapperClassNames?: string;
  itemClassNames?: string;
  viewportClassName?: string;
  alignment?: 'center' | 'end' | 'start';
  side?: 'top' | 'right' | 'bottom' | 'left';
  onOptionSelect?: (value: Option | Option[] | undefined) => void;
  optionItem?: (option: Option) => React.ReactNode;
  tagItem?: (option: Option) => React.ReactNode;
  multiple?: boolean;
  varient?: 'default' | 'inline-multiple';
  clearable?: boolean;
  searchable?: boolean;
  searchKeyword?: string;
  searchLoading?: boolean;
  onSearch?: (v: string) => void;
  searchImmediate?: boolean;
  prefix?: React.ReactNode;
  triggerIconClassNames?: string;
  searchInputRef?: RefObject<HTMLInputElement>;
  triggerTextRender?: (value: Option | Option[] | undefined) => React.ReactNode;
  triggerIcon?: IconName;
  triggerWrapperClassName?: string;
  triggerPill?: boolean;
  tagWrapperClassName?: string;
  tagClassName?: string;
  tagPillColor?: PillProps['color'];
  triggerPillProps?: PillProps;
  iconSize?: number;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  showScrollButtons?: boolean;
  maxTags?: number;
  trigger?: React.ReactNode;
  portalContainer?: HTMLElement | null;
  footer?: React.ReactNode | ((close: () => void) => React.ReactNode);
};

const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  placeholder = 'Select',
  noDataText = 'No Data',
  defaultValue,
  triggerClassNames,
  contentClassNames,
  itemClassNames,
  wrapperClassNames,
  triggerIconClassNames,
  viewportClassName,
  alignment = 'start',
  side,
  optionItem,
  tagItem,
  onOptionSelect = () => {},
  multiple = false,
  varient = 'default',
  triggerTextRender,
  clearable = false,
  prefix,
  searchKeyword,
  searchLoading,
  searchable,
  onSearch,
  searchImmediate = false,
  searchInputRef,
  triggerIcon,
  triggerWrapperClassName,
  triggerPill,
  triggerPillProps,
  tagWrapperClassName,
  tagClassName,
  tagPillColor = 'neutral',
  iconSize = 4,
  onClick,
  showScrollButtons = false,
  maxTags = 2,
  trigger,
  portalContainer,
  footer,
  ...props
}) => {
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [localSearchKeyword, setLocalSearchKeyword] = useState('');
  const localSearchInputRef = useRef<HTMLInputElement>(null);
  const inlineTagsRef = useRef<HTMLDivElement>(null);
  const inlineTagsMeasureRef = useRef<HTMLDivElement>(null);
  const searchtimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [isOpen, setIsOpen] = useState(props.defaultOpen || false);
  const [inlineVisibleTagCount, setInlineVisibleTagCount] = useState(maxTags);
  const isInlineMultiple = multiple && varient === 'inline-multiple';
  const resolvedTagItem = tagItem ?? optionItem;

  const fullOptions = useMemo(() => {
    const newOptions = [...options];
    if (
      searchable &&
      !multiple &&
      selectedOption?.id &&
      !newOptions.find(o => o.id === selectedOption?.id)
    ) {
      newOptions.unshift(selectedOption);
    }

    return newOptions;
  }, [options, selectedOption, multiple, searchable]);

  useEffect(() => {
    if (multiple) {
      const selectedValues = Array.isArray(defaultValue)
        ? defaultValue
        : defaultValue !== undefined && defaultValue !== ''
          ? [defaultValue]
          : [];
      setSelectedOptions(prevSelected => {
        // Keep already-resolved selections as-is (with their full rendering data)
        // instead of re-deriving them from fullOptions, which may be a search-narrowed
        // list that no longer contains a previously selected option.
        const stillSelected = prevSelected.filter(opt =>
          selectedValues.some(value => value.toString() === opt.id.toString())
        );
        const stillSelectedIds = new Set(stillSelected.map(opt => opt.id.toString()));
        const newlyResolved = fullOptions.filter(
          opt =>
            selectedValues.some(value => value.toString() === opt.id.toString()) &&
            !stillSelectedIds.has(opt.id.toString())
        );
        if (!newlyResolved.length && stillSelected.length === prevSelected.length) {
          return prevSelected;
        }
        return [...stillSelected, ...newlyResolved];
      });
    } else {
      const newSelectedOption =
        fullOptions.find(opt => {
          return (
            defaultValue !== undefined &&
            defaultValue !== null &&
            String(opt.id) === String(defaultValue)
          );
        }) || null;
      setSelectedOption(newSelectedOption);
    }
  }, [defaultValue, fullOptions, multiple]);

  const handleSelect = (id: string) => {
    if (id) {
      const selectedOption = options.find(opt => opt.id === id) || null;
      setSelectedOption(selectedOption);

      if (multiple) {
        setSelectedOptions(prevSelected => {
          let v: Option[] = [];
          if (prevSelected.find(opt => opt.id === id)) {
            v = prevSelected.filter(opt => opt.id !== id);
          } else {
            v = [...prevSelected];
            if (selectedOption) {
              v.push(selectedOption);
            }
          }
          // defer onOptionSelect outside the state updater to avoid setState-during-render
          setTimeout(() => onOptionSelect(v), 0);
          return v;
        });
      } else {
        setSelectedOptions([]);
        onOptionSelect(selectedOption || undefined);
      }
    }
  };

  const filteredOptions = useMemo(() => {
    const visibleOptions = fullOptions.filter(v => v.show !== false);
    const effectiveSearchKeyword = searchKeyword ?? localSearchKeyword;

    if (!searchable || onSearch || !effectiveSearchKeyword.trim()) {
      return visibleOptions;
    }

    const normalizedSearchKeyword = effectiveSearchKeyword.trim().toLowerCase();
    return visibleOptions.filter(option => {
      return [option.id, option.value].some(value =>
        value.toString().toLowerCase().includes(normalizedSearchKeyword)
      );
    });
  }, [fullOptions, localSearchKeyword, onSearch, searchKeyword, searchable]);

  useEffect(() => {
    if (!searchable || !isOpen) return;

    const input = (searchInputRef || localSearchInputRef).current;
    if (!input) return;

    const frameId = requestAnimationFrame(() => {
      if (document.activeElement !== input) {
        input.focus();
      }
    });

    return () => cancelAnimationFrame(frameId);
  }, [
    filteredOptions.length,
    isOpen,
    localSearchKeyword,
    searchInputRef,
    searchKeyword,
    searchable,
  ]);

  useLayoutEffect(() => {
    if (!isInlineMultiple) return;

    const calculateVisibleTags = () => {
      const container = inlineTagsRef.current;
      const measureContainer = inlineTagsMeasureRef.current;

      if (!container || !measureContainer || selectedOptions.length === 0) {
        setInlineVisibleTagCount(maxTags);
        return;
      }

      const availableWidth = container.clientWidth;
      const optionPills = Array.from(
        measureContainer.querySelectorAll<HTMLElement>('[data-inline-select-pill="option"]')
      );
      const countPill = measureContainer.querySelector<HTMLElement>(
        '[data-inline-select-pill="count"]'
      );
      const gap = parseFloat(window.getComputedStyle(measureContainer).columnGap || '0') || 0;

      if (availableWidth <= 0 || optionPills.length === 0) {
        setInlineVisibleTagCount(maxTags);
        return;
      }

      let usedWidth = 0;
      let nextVisibleCount = 0;

      for (let index = 0; index < selectedOptions.length; index += 1) {
        const remainingCount = selectedOptions.length - (index + 1);
        const countPillWidth = remainingCount > 0 && countPill ? countPill.offsetWidth + gap : 0;
        const nextWidth = optionPills[index].offsetWidth + (index > 0 ? gap : 0);

        if (usedWidth + nextWidth + countPillWidth > availableWidth) {
          break;
        }

        usedWidth += nextWidth;
        nextVisibleCount += 1;
      }

      setInlineVisibleTagCount(Math.max(1, nextVisibleCount));
    };

    calculateVisibleTags();

    const resizeObserver = new ResizeObserver(calculateVisibleTags);
    if (inlineTagsRef.current) {
      resizeObserver.observe(inlineTagsRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [isInlineMultiple, maxTags, selectedOptions]);

  const testId = `select-${props.name}`;
  const inlineVisibleOptions = isInlineMultiple
    ? selectedOptions.slice(0, inlineVisibleTagCount)
    : selectedOptions.slice(0, maxTags);
  const renderSingleTriggerText = () => {
    const rendered = triggerTextRender?.(selectedOption || undefined);
    if (rendered !== undefined && rendered !== null) return rendered;
    return selectedOption ? selectedOption.value : placeholder;
  };

  const isControlledOpen = props.open !== undefined;
  const currentOpen = isControlledOpen ? props.open : isOpen;

  return (
    <div className={`relative ${wrapperClassNames}`}>
      <SelectRoot
        {...props}
        open={currentOpen}
        value={multiple ? '' : defaultValue?.toString()}
        onValueChange={v => {
          handleSelect(v);
          if (!multiple) {
            setIsOpen(false);
          }
        }}
        onOpenChange={o => {
          if (multiple && !o) return;
          setIsOpen(o);
          if (o) {
            const hadSearchKeyword = !!(searchKeyword ?? localSearchKeyword);
            setLocalSearchKeyword('');
            if (hadSearchKeyword) {
              setTimeout(() => {
                onSearch?.('');
              }, 1000);
            }
          }

          if (props.onOpenChange) {
            props.onOpenChange(o);
          }
        }}
      >
        {trigger ? (
          <SelectTrigger asChild onClick={onClick}>
            {trigger}
          </SelectTrigger>
        ) : (
          <SelectTrigger
            data-testid={testId}
            className={cn(
              triggerPill
                ? 'inline-flex h-auto max-w-full appearance-none items-center justify-start whitespace-nowrap border-none bg-transparent p-0 text-sm text-foreground outline-none disabled:cursor-not-allowed disabled:opacity-50'
                : cn(
                    'flex h-[38px] w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-white dark:bg-input/30 px-3 py-2 text-sm text-foreground outline-none transition-all duration-300 enabled:active:scale-[0.98] enabled:hover:bg-accent enabled:hover:text-accent-foreground placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white dark:disabled:hover:bg-input/30 disabled:hover:text-muted-foreground relative [&>span]:line-clamp-1 overflow-hidden',
                    prefix ? 'pl-0' : 'pl-3'
                  ),
              triggerClassNames
            )}
            aria-label={placeholder}
            onClick={e => {
              if (props.disabled) return;
              setIsOpen(true);
              onClick?.(e);
            }}
          >
            {triggerPill ? (
              <Pill
                {...triggerPillProps}
                className={cn(triggerPillProps?.className, 'max-w-full min-w-0 overflow-hidden')}
              >
                <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap normal-case">
                  {renderSingleTriggerText()}
                </span>
                <Icon name="arrow-down-s-fill" className="w-4 h-4" />
              </Pill>
            ) : (
              <>
                {prefix && (
                  <div className="px-3 py-2 border-r border-border mr-3 text-muted-foreground">
                    {prefix}
                  </div>
                )}
                <div
                  className={cn(
                    'flex py-2 gap-2 items-center w-full justify-between',
                    triggerWrapperClassName
                  )}
                >
                  {multiple ? (
                    isInlineMultiple ? (
                      triggerTextRender ? (
                        <div className="text-ellipsis overflow-hidden whitespace-nowrap">
                          {selectedOptions.length === 0 ? (
                            <span className="text-muted-foreground">{placeholder}</span>
                          ) : (
                            triggerTextRender(selectedOptions)
                          )}
                        </div>
                      ) : (
                        <div
                          ref={inlineTagsRef}
                          className="relative flex min-w-0 w-full flex-nowrap gap-1 overflow-hidden"
                        >
                          <div
                            ref={inlineTagsMeasureRef}
                            className="invisible pointer-events-none absolute left-0 top-0 flex h-0 max-w-none flex-nowrap gap-1 overflow-hidden"
                            aria-hidden="true"
                          >
                            {selectedOptions.map((option, index) => (
                              <div
                                key={`${option.id}-${index}-measure`}
                                data-inline-select-pill="option"
                                className="shrink-0"
                              >
                                {resolvedTagItem ? (
                                  resolvedTagItem(option)
                                ) : (
                                  <Pill
                                    variant="soft"
                                    color="neutral"
                                    className="w-fit max-w-[45%] shrink-0 !justify-start overflow-hidden normal-case"
                                    rounded={false}
                                  >
                                    <span className="min-w-0 flex-1 truncate whitespace-nowrap text-left">
                                      {option.value}
                                    </span>
                                  </Pill>
                                )}
                              </div>
                            ))}
                            <div data-inline-select-pill="count" className="shrink-0">
                              <Pill
                                variant="soft"
                                color="neutral"
                                rounded={false}
                                className="shrink-0 pr-1 normal-case whitespace-nowrap"
                              >
                                +{selectedOptions.length}
                              </Pill>
                            </div>
                          </div>
                          {selectedOptions.length === 0 ? (
                            <span className="text-muted-foreground">{placeholder}</span>
                          ) : null}
                          {inlineVisibleOptions.map((option, index) =>
                            resolvedTagItem ? (
                              <span key={index} className="min-w-0 overflow-hidden">
                                {resolvedTagItem(option)}
                              </span>
                            ) : (
                              <Pill
                                key={index}
                                variant="soft"
                                color="neutral"
                                className="w-fit max-w-[45%] shrink-0 !justify-start overflow-hidden normal-case"
                                rounded={false}
                              >
                                <span className="min-w-0 flex-1 truncate whitespace-nowrap text-left">
                                  {option.value}
                                </span>
                              </Pill>
                            )
                          )}
                          {selectedOptions.length > inlineVisibleTagCount && (
                            <Pill
                              variant="soft"
                              color="neutral"
                              rounded={false}
                              className="shrink-0 pr-1 normal-case whitespace-nowrap"
                            >
                              +{selectedOptions.length - inlineVisibleTagCount}
                            </Pill>
                          )}
                        </div>
                      )
                    ) : (
                      <span className="text-muted-foreground">{placeholder}</span>
                    )
                  ) : (
                    <div className="text-ellipsis overflow-hidden whitespace-nowrap ">
                      {selectedOption ? (
                        renderSingleTriggerText()
                      ) : (
                        <span className="text-muted-foreground">{placeholder}</span>
                      )}
                    </div>
                  )}
                  <SelectTriggerIcon asChild>
                    <Icon
                      name={triggerIcon || 'chevron-down'}
                      className={cn(
                        '!text-muted-foreground shrink-0 w-4 h-4',
                        triggerIconClassNames
                      )}
                    />
                  </SelectTriggerIcon>
                </div>
              </>
            )}
          </SelectTrigger>
        )}

        <span
          data-testid={`${testId}-clear`}
          className={`absolute right-7 top-0 h-[38px] w-9 items-center justify-center cursor-pointer text-muted-foreground opacity-50 hover:opacity-100 ${clearable && (selectedOption || selectedOptions.length > 0) ? 'flex' : 'hidden'}`}
          onClick={() => {
            setSelectedOption(null);
            setSelectedOptions([]);
            if (multiple) {
              onOptionSelect([]);
            } else {
              onOptionSelect(undefined);
            }
          }}
        >
          <Icon size={4} name="cross-fill" />
        </span>

        <SelectPortal container={portalContainer ?? undefined}>
          <SelectContent
            data-custom-select-content=""
            className={cn(
              'relative pointer-events-auto flex flex-col text-muted-foreground max-h-[min(300px,var(--radix-select-content-available-height))] overflow-hidden rounded border border-border bg-background shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state:closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state:closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[51] min-w-[var(--radix-popper-anchor-width)]',
              contentClassNames
            )}
            position="popper"
            side={side}
            sideOffset={3}
            align={alignment}
            onPointerDownOutside={() => {
              if (multiple) setIsOpen(false);
            }}
            onEscapeKeyDown={() => {
              if (multiple) setIsOpen(false);
            }}
          >
            {searchable && (
              <div
                className="px-3 mt-3 mb-2"
                onPointerDown={event => event.stopPropagation()}
                onMouseDown={event => event.stopPropagation()}
                onClick={event => event.stopPropagation()}
              >
                <Input
                  defaultValue={searchKeyword}
                  key={'search'}
                  onChange={e => {
                    const v = e.target.value;
                    setLocalSearchKeyword(v);
                    if (searchImmediate) {
                      onSearch?.(v);
                    } else {
                      clearTimeout(searchtimer.current);
                      searchtimer.current = setTimeout(() => {
                        onSearch?.(v);
                      }, 1000);
                    }
                  }}
                  onPointerDown={e => e.stopPropagation()}
                  onMouseDown={e => e.stopPropagation()}
                  onClick={e => e.stopPropagation()}
                  onKeyDown={e => {
                    e.stopPropagation();
                  }}
                  ref={searchInputRef || localSearchInputRef}
                  prefix={<Icon size={5} name="search" />}
                  className="h-9 py-1.5"
                  wrapperClassName="h-9 rounded-md"
                />
              </div>
            )}

            {showScrollButtons && (
              <SelectScrollUpButton className="flex items-center justify-center">
                <Icon name="arrow-up-s-line" size={4} />
              </SelectScrollUpButton>
            )}

            <SelectPrimitive.Viewport
              className={cn('flex-1 min-h-0 overflow-y-auto w-full p-1', viewportClassName)}
            >
              {filteredOptions.length === 0
                ? !searchLoading && (
                    <span className="p-2 text-center block w-full text-sm text-muted-foreground">
                      {noDataText}
                    </span>
                  )
                : filteredOptions.map((option, index) => {
                    const isSelected =
                      multiple && selectedOptions.find(opt => opt.id === option.id);
                    const group =
                      typeof option.meta?.group === 'string' ? option.meta.group : undefined;
                    const previousMeta = filteredOptions[index - 1]?.meta;
                    const previousGroup =
                      typeof previousMeta?.group === 'string' ? previousMeta.group : undefined;

                    return (
                      <React.Fragment key={index + option.id.toString()}>
                        {group && group !== previousGroup && (
                          <div
                            className={cn(
                              'px-2 py-1.5 text-[11px] font-medium uppercase leading-4 text-[var(--sidebar-ring)]',
                              previousGroup ? 'mt-2' : ''
                            )}
                          >
                            {group}
                          </div>
                        )}
                        <SelectItem
                          data-testid={`${testId}-item-${option.id}`}
                          value={option.id.toString()}
                          disabled={Boolean(option.disabled)}
                          onPointerLeave={event => event.preventDefault()}
                          onPointerMove={event => event.preventDefault()}
                          className={cn(
                            'relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm py-1.5 pl-2 text-sm outline-none transition-all duration-300 focus:bg-accent focus:text-accent-foreground hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                            multiple ? 'pr-2' : 'pr-8',
                            itemClassNames
                          )}
                        >
                          {multiple && (
                            <span
                              className={cn(
                                'flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border',
                                isSelected
                                  ? 'border-primary bg-primary text-primary-foreground'
                                  : 'border-border'
                              )}
                            >
                              {isSelected && <Icon name="check" size={3} />}
                            </span>
                          )}
                          {option.icon && (
                            <Icon name={option.icon} size={5} {...option.iconProps} />
                          )}
                          {option.prefix && option.prefix}
                          {optionItem ? optionItem(option) : <span>{option.value}</span>}
                          {!multiple && selectedOption?.id === option.id && (
                            <SelectPrimitive.ItemIndicator className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                              <Icon name="check" size={3.5} />
                            </SelectPrimitive.ItemIndicator>
                          )}
                        </SelectItem>
                      </React.Fragment>
                    );
                  })}
            </SelectPrimitive.Viewport>

            {showScrollButtons && (
              <SelectScrollDownButton className="flex items-center justify-center">
                <Icon name="arrow-down-s-line" size={4} />
              </SelectScrollDownButton>
            )}

            {searchLoading && filteredOptions.length === 0 && (
              <div className="min-h-[300px]">
                <div className="absolute top-[60px] inset-0 bg-background ">
                  <Spinner></Spinner>
                </div>
              </div>
            )}

            {footer && (
              <div
                className="border-t border-border p-1 shrink-0"
                onPointerDown={event => event.stopPropagation()}
                onMouseDown={event => event.stopPropagation()}
              >
                {typeof footer === 'function'
                  ? footer(() => {
                      setIsOpen(false);
                      props.onOpenChange?.(false);
                    })
                  : footer}
              </div>
            )}
          </SelectContent>
        </SelectPortal>
      </SelectRoot>

      {multiple && selectedOptions.length > 0 && varient === 'default' && (
        <div className={cn('flex flex-wrap mt-2 gap-1', tagWrapperClassName)}>
          {selectedOptions.map((option, index) => (
            <Pill
              key={index}
              variant="soft"
              color={tagPillColor}
              className={cn(
                'max-w-full min-w-0 shrink !justify-start overflow-hidden pr-1 normal-case',
                tagClassName
              )}
            >
              <span className="min-w-0 flex-1 truncate whitespace-nowrap text-left">
                {option.value}
              </span>
              <span
                className="flex shrink-0 cursor-pointer"
                role="button"
                tabIndex={0}
                data-testid={`${testId}-item-${option.id}-remove`}
                onClick={() => {
                  const filtered = selectedOptions.filter(opt => opt.id !== option.id);
                  setSelectedOptions(filtered);
                  onOptionSelect(filtered);
                }}
              >
                <Icon name="cross-fill" size={iconSize} />
              </span>
            </Pill>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
